import { Button } from "@dash/form";
import { SectionHeader } from "@dash/page";
import { DocsSelectDialog } from "@dash/select";
import { useTheme } from "@emotion/react";
import type { FC } from "react";
import { useMemo, useState } from "react";
import { MdAdd } from "react-icons/md";
import { useQueryClient } from "react-query";

import { CustomerDoc, isDocOfType } from "@megaron/docs-contracts";
import { useClientManager } from "@megaron/react-clients";
import type { TicketCustomerLinkDto } from "@megaron/support-contracts";
import { Uuid } from "@megaron/uuid";

import { CrmCustomerCard } from "../crm/CrmCustomerCard";
import { SupportFieldLabel } from "../shared/SupportFieldLabel";
import { getReporterCustomerUuidFromLinks } from "../supportTicketCustomerUtils";
import { SupportTicketManualReporterCard } from "./SupportTicketManualReporterCard";

type Props = {
  ticketId: Uuid;
  reporterName: string;
  reporterEmail: string;
  ticketCustomers: TicketCustomerLinkDto[];
  isEditable: boolean;
};

function matchCustomerByEmail(items: CustomerDoc[], email: string): CustomerDoc | undefined {
  const lower = email.trim().toLowerCase();
  return items.find((customer) => customer.email?.trim().toLowerCase() === lower);
}

function hasManualReporterData(reporterName: string, reporterEmail: string): boolean {
  return reporterName.trim().length > 0 || reporterEmail.trim().length > 0;
}

function hasReporterContactOverride(
  reporterName: string,
  reporterEmail: string,
  customer: CustomerDoc,
): boolean {
  const trimmedName = reporterName.trim();
  const trimmedEmail = reporterEmail.trim().toLowerCase();
  const customerName = customer.name.trim();
  const customerEmail = customer.email?.trim().toLowerCase() ?? "";
  if (trimmedName && trimmedName !== customerName) return true;
  if (trimmedEmail && trimmedEmail !== customerEmail) return true;
  return false;
}

export const SupportTicketCustomersSection: FC<Props> = ({
  ticketId,
  reporterName,
  reporterEmail,
  ticketCustomers,
  isEditable,
}) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const reporterCustomerUuid = getReporterCustomerUuidFromLinks(ticketCustomers);
  const assignedCustomerUuids = ticketCustomers
    .filter((customer) => !customer.isReporter)
    .map((customer) => customer.customerUuid);
  const showManualReporter = !reporterCustomerUuid && hasManualReporterData(reporterName, reporterEmail);

  const reporterDocsQuery = useClientManager("docs")
    .retrieveDocs()
    .useQuery(
      { docIds: reporterCustomerUuid ? [`customer:${reporterCustomerUuid}`] : [] },
      { enabled: !!reporterCustomerUuid },
    );

  const assignedDocsQuery = useClientManager("docs")
    .retrieveDocs()
    .useQuery(
      { docIds: assignedCustomerUuids.map((uuid) => `customer:${uuid}`) },
      { enabled: assignedCustomerUuids.length > 0 },
    );

  const searchQuery = useClientManager("docs")
    .searchCustomers()
    .useQuery(
      { searchText: reporterEmail.trim(), limit: 10 },
      { enabled: showManualReporter && !!reporterEmail.trim() },
    );

  const addMutation = useClientManager("support")
    .addTicketCustomer()
    .useMutation({
      onSuccess: () => queryClient.invalidateQueries({ queryKey: ["support", "getTicket"] }),
    });

  const removeMutation = useClientManager("support")
    .removeTicketCustomer()
    .useMutation({
      onSuccess: () => queryClient.invalidateQueries({ queryKey: ["support", "getTicket"] }),
    });

  const reporterCustomer = useMemo((): CustomerDoc | undefined => {
    const doc = reporterDocsQuery.data?.[0];
    if (!doc || !isDocOfType("customer")(doc)) return undefined;
    return doc;
  }, [reporterDocsQuery.data]);

  const assignedCustomers = useMemo((): CustomerDoc[] => {
    if (assignedCustomerUuids.length === 0) return [];
    return (assignedDocsQuery.data ?? []).filter(
      (doc): doc is CustomerDoc => doc !== null && isDocOfType("customer")(doc),
    );
  }, [assignedCustomerUuids.length, assignedDocsQuery.data]);

  const emailMatchedCustomer = useMemo((): CustomerDoc | undefined => {
    if (!showManualReporter) return undefined;
    return matchCustomerByEmail(searchQuery.data?.items ?? [], reporterEmail);
  }, [reporterEmail, searchQuery.data?.items, showManualReporter]);

  const isLoading =
    (reporterCustomerUuid ? reporterDocsQuery.isLoading : false) ||
    (assignedCustomerUuids.length > 0 ? assignedDocsQuery.isLoading : false) ||
    (showManualReporter && !!reporterEmail.trim() ? searchQuery.isLoading : false);

  if (isLoading) {
    return (
      <div
        css={{
          padding: "0.75rem 1rem",
          border: `1px solid ${theme.colors.border}`,
          borderRadius: theme.smallBorderRadius,
          fontSize: "14px",
          color: theme.colors.secondaryText,
        }}
      >
        Ładowanie danych klientów...
      </div>
    );
  }

  const hasReporterBlock = !!reporterCustomer || showManualReporter;
  const hasAssignedCustomers = assignedCustomers.length > 0;

  return (
    <>
      <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        <SupportFieldLabel>Zgłaszający</SupportFieldLabel>
        {reporterCustomer ? (
          <>
            <CrmCustomerCard
              customer={reporterCustomer}
              variant="reporter"
              caption="Zgłaszający · Klient w CRM"
            />
            {hasReporterContactOverride(reporterName, reporterEmail, reporterCustomer) ? (
              <div
                css={{
                  fontSize: "0.75rem",
                  color: theme.colors.secondaryText,
                  paddingLeft: "0.25rem",
                }}
              >
                Kontakt na zgłoszeniu:{" "}
                <span css={{ color: theme.colors.primary, fontWeight: 600 }}>
                  {reporterName.trim() || reporterEmail.trim()}
                </span>
                {reporterName.trim() && reporterEmail.trim() ? ` · ${reporterEmail.trim()}` : null}
              </div>
            ) : null}
          </>
        ) : null}
        {showManualReporter ? (
          <SupportTicketManualReporterCard
            reporterName={reporterName}
            reporterEmail={reporterEmail}
            emailMatchedCustomer={emailMatchedCustomer}
          />
        ) : null}
        {!hasReporterBlock ? (
          <div
            css={{
              padding: "0.75rem 1rem",
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.smallBorderRadius,
              fontSize: "14px",
              color: theme.colors.secondaryText,
            }}
          >
            Brak danych zgłaszającego
          </div>
        ) : null}
      </div>
      <div css={{ display: "flex", flexDirection: "column", gap: "1rem", marginTop: "1rem" }}>
        {hasAssignedCustomers ? (
          <>
            <SectionHeader
              isHr
              css={{ h2: { fontSize: "16px" } }}
              actions={
                isEditable ? (
                  <Button size="small" variant="outline" onClick={() => setAddDialogOpen(true)}>
                    Przypisz
                  </Button>
                ) : undefined
              }
            >
              Przypisani klienci
            </SectionHeader>
            <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {assignedCustomers.map((customer) => (
                <CrmCustomerCard
                  key={customer.uuid}
                  customer={customer}
                  caption="Przypisany klient"
                  onDetach={
                    isEditable
                      ? () => removeMutation.mutate({ id: ticketId, customerUuid: customer.uuid })
                      : undefined
                  }
                />
              ))}
            </div>
          </>
        ) : (
          isEditable && (
            <Button
              variant="outline"
              size="small"
              icon={<MdAdd size={16} />}
              onClick={() => setAddDialogOpen(true)}
            >
              Przypisz klienta z CRM
            </Button>
          )
        )}
      </div>
      <DocsSelectDialog
        docType={["customer"]}
        variant="single-select"
        label="Przypisz klienta z CRM"
        noTrigger
        isOpen={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSelect={(option) => {
          if (option && option.docType === "customer" && option.value) {
            setAddDialogOpen(false);
            addMutation.mutate({ id: ticketId, customerUuid: Uuid(option.value), isReporter: false });
          }
        }}
      />
    </>
  );
};
